"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, ArrowUpRight, ChartBar as BarChart3, Calendar, CircleCheck as CheckCircle2, ChevronRight, Clock, DollarSign, Globe as Globe2, Percent, RefreshCw, ShieldCheck, TrendingUp, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function ClientPortalPreview() {
  const [selectedRange, setSelectedRange] = useState<"7D" | "30D" | "90D">("30D");
  const kpiData = [{
    label: "Average Daily Rate (ADR)",
    value: "₹6,450",
    change: "+18.4%",
    subtext: "vs previous period",
    icon: DollarSign
  }, {
    label: "Occupancy Rate",
    value: "82.6%",
    change: "+7.2%",
    subtext: "comp-set avg 75.4%",
    icon: Percent
  }, {
    label: "RevPAR",
    value: "₹5,327",
    change: "+24.1%",
    subtext: "peak yield window",
    icon: TrendingUp
  }, {
    label: "OTA Channel Mix",
    value: "4 Active",
    badge: "Balanced",
    subtext: "0 parity disputes",
    icon: Globe2
  }];
  const channelBreakdown = [{
    channel: "Booking.com",
    revenue: "₹4.8L",
    share: 38,
    status: "Optimal parity",
    statusVariant: "secondary" as const
  }, {
    channel: "MakeMyTrip / Goibibo",
    revenue: "₹4.1L",
    share: 32,
    status: "High demand",
    statusVariant: "secondary" as const
  }, {
    channel: "Agoda",
    revenue: "₹2.3L",
    share: 18,
    status: "International pickup",
    statusVariant: "outline" as const
  }, {
    channel: "Direct Booking Engine",
    revenue: "₹1.5L",
    share: 12,
    status: "Growing +5%",
    statusVariant: "secondary" as const
  }];
  const healthCheckpoints = [{
    title: "Rate Parity Discrepancies",
    desc: "Instant detection across major OTAs"
  }, {
    title: "OTA Ranking & Visibility",
    desc: "Weekly indexing tracker & tag audit"
  }, {
    title: "Lead Time & LOS Rules",
    desc: "Min-stay and advance purchase optimization"
  }, {
    title: "Destination Spike Alerts",
    desc: "Local surge & compression monitoring"
  }];

  // Static bars representing 30-Day dynamic surge and realized revenue
  const chartBars = [{
    day: "01",
    actual: 55,
    surge: 70
  }, {
    day: "03",
    actual: 62,
    surge: 75
  }, {
    day: "05",
    actual: 70,
    surge: 80
  }, {
    day: "07",
    actual: 58,
    surge: 65
  }, {
    day: "09",
    actual: 82,
    surge: 90
  }, {
    day: "11",
    actual: 88,
    surge: 94
  }, {
    day: "13",
    actual: 76,
    surge: 82
  }, {
    day: "15",
    actual: 84,
    surge: 92
  }, {
    day: "17",
    actual: 95,
    surge: 100
  }, {
    day: "19",
    actual: 90,
    surge: 96
  }, {
    day: "21",
    actual: 68,
    surge: 74
  }, {
    day: "23",
    actual: 78,
    surge: 85
  }, {
    day: "25",
    actual: 85,
    surge: 92
  }, {
    day: "27",
    actual: 92,
    surge: 98
  }, {
    day: "30",
    actual: 98,
    surge: 100
  }];
  return <section id="clientportalpreview" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden isolate">
      {/* Background visual accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[48rem] h-[48rem] rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 -right-48 w-96 h-96 rounded-full bg-primary/5 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/15 border border-secondary/30 text-xs font-medium uppercase tracking-wider text-secondary-foreground mb-4">
            <span className="size-2 rounded-full bg-secondary animate-pulse" />
            Illustrative Preview &mdash; Client Portal
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance">
            See what your automated hotel dashboard looks like
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed text-pretty">
            When you partner with HotelRevBaBa, we establish continuous rate tracking,
            OTA parity monitoring, and demand forecasting so you always know your
            property&apos;s daily pickup and revenue pace.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-sm" asChild>
              <Link to="#">
                Book a consultation
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="#">Explore revenue services</Link>
            </Button>
          </div>
        </div>

        {/* Dashboard Shell Mockup */}
        <div className="relative rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
          {/* Top Window Bar */}
          <div className="bg-primary text-primary-foreground px-4 py-3.5 flex flex-wrap items-center justify-between gap-4 border-b border-primary/20">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5" aria-hidden="true">
                <span className="size-3 rounded-full bg-destructive/80 inline-block" />
                <span className="size-3 rounded-full bg-secondary/80 inline-block" />
                <span className="size-3 rounded-full bg-emerald-500/80 inline-block" />
              </div>
              <div className="h-4 w-px bg-primary-foreground/20 hidden sm:block" />
              <div className="flex items-center gap-2 text-xs sm:text-sm font-medium">
                <ShieldCheck className="size-4 text-secondary" />
                <span>Sample Boutique Resort &amp; Spa (Simulated View)</span>
              </div>
            </div>

            <div className="flex items-center gap-3 ml-auto text-xs">
              <div className="hidden md:flex items-center gap-1.5 text-primary-foreground/75">
                <RefreshCw className="size-3 text-secondary animate-spin" style={{
                animationDuration: "6s"
              }} />
                <span>Syncing OTAs every 15 min</span>
              </div>

              {/* Range Selector Toggles */}
              <div className="inline-flex rounded-md bg-primary-foreground/10 p-0.5 border border-primary-foreground/15">
                {(["7D", "30D", "90D"] as const).map(range => <Button key={range} type="button" onClick={() => setSelectedRange(range)} className={`h-auto px-2.5 py-1 text-xs font-medium rounded transition-colors ${selectedRange === range ? "bg-secondary text-secondary-foreground font-semibold shadow-xs hover:bg-secondary" : "bg-transparent text-primary-foreground/80 hover:text-primary-foreground hover:bg-transparent shadow-none"}`}>
                    {range}
                  </Button>)}
              </div>
            </div>
          </div>

          {/* Inner Content Area */}
          <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-background/50">
            {/* Alert Banner */}
            <div className="rounded-lg border border-secondary/30 bg-secondary/10 px-4 py-3 text-sm flex items-start gap-3">
              <AlertCircle className="size-5 text-secondary shrink-0 mt-0.5" />
              <div className="text-foreground text-xs sm:text-sm">
                <strong className="font-semibold text-foreground">Demonstration Dashboard:</strong> The metrics and charts below illustrate how Sunil Singh and the HotelRevBaBa team track pricing elasticity, OTA parity, and pace metrics for client properties.
              </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {kpiData.map((kpi, idx) => <Card key={idx} data-index={idx} className="bg-card border-border shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        {kpi.label}
                      </span>
                      <div className="p-2 rounded-md bg-secondary/15 text-secondary-foreground">
                        <kpi.icon className="size-4" />
                      </div>
                    </div>
                    <div className="mt-3 flex items-baseline justify-between gap-2">
                      <div className="text-2xl font-bold font-serif tracking-tight text-foreground">
                        {kpi.value}
                      </div>
                      {kpi.change && <span className="inline-flex items-center text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          {kpi.change}
                        </span>}
                      {kpi.badge && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                          {kpi.badge}
                        </Badge>}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">{kpi.subtext}</p>
                  </CardContent>
                </Card>)}
            </div>

            {/* Main Layout Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Trajectory Chart + Health Checks (7 cols) */}
              <div className="lg:col-span-7 space-y-6">
                {/* 30-Day Trajectory Area */}
                <Card className="bg-card border-border shadow-sm">
                  <CardHeader className="pb-3 border-b border-border/60">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <CardTitle className="text-base sm:text-lg font-bold font-serif">
                          30-Day Revenue &amp; ADR Trajectory
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Live market dynamic pricing adjustments vs competitor set
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs px-2.5 py-1">
                        Pacing +22% vs 2024
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-6">
                    {/* Visual Bar Chart */}
                    <div className="h-44 sm:h-52 w-full flex items-end justify-between gap-1.5 sm:gap-2 px-1 pb-2 border-b border-border">
                      {chartBars.map((bar, i) => <div key={i} data-index={i} className="flex-1 flex flex-col items-center gap-1 group relative h-full justify-end">
                          <div className="w-full rounded-t-sm bg-secondary transition-all duration-300 group-hover:brightness-110" style={{
                        height: `${bar.surge}%`
                      }} title={`Day ${bar.day}: Surge ₹${(bar.surge * 75).toLocaleString()}`} />
                          <div className="w-full rounded-t-sm bg-primary/75 -mt-full transition-all group-hover:bg-primary" style={{
                        height: `${bar.actual}%`
                      }} />
                          <span className="text-[10px] text-muted-foreground font-mono mt-1">
                            {bar.day}
                          </span>
                        </div>)}
                    </div>

                    {/* Chart Legend */}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <span className="size-2.5 rounded-xs bg-primary" />
                          <span>Realized Revenue</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="size-2.5 rounded-xs bg-secondary" />
                          <span>Recommended Surge Pricing</span>
                        </div>
                      </div>
                      <span className="text-[11px] font-medium text-foreground">
                        High Elasticity Weekend Uplift Detected
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Automated Health Checks Panel */}
                <Card className="bg-card border-border shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg font-bold font-serif flex items-center gap-2">
                      <CheckCircle2 className="size-5 text-secondary" />
                      Automated Health Checks in Client Portal
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {healthCheckpoints.map((item, idx) => <div key={idx} data-index={idx} className="p-3 rounded-lg border border-border/70 bg-muted/30 flex items-start gap-3">
                          <div className="size-2 rounded-full bg-secondary mt-1.5 shrink-0" />
                          <div>
                            <div className="text-xs sm:text-sm font-semibold text-foreground">
                              {item.title}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {item.desc}
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Channel Breakdown + Expert Banner (5 cols) */}
              <div className="lg:col-span-5 space-y-6 flex flex-col justify-between">
                {/* Channel Distribution Breakdown */}
                <Card className="bg-card border-border shadow-sm flex-1">
                  <CardHeader className="pb-3 border-b border-border/60">
                    <CardTitle className="text-base sm:text-lg font-bold font-serif">
                      Channel Distribution Breakdown
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Commission vs direct yield optimization
                    </p>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    {channelBreakdown.map((item, idx) => <div key={idx} data-index={idx} className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="font-medium text-foreground">
                            {item.channel}
                          </span>
                          <span className="font-bold font-mono text-foreground">
                            {item.revenue}
                          </span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-secondary transition-all" style={{
                        width: `${item.share}%`
                      }} />
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>{item.share}% share</span>
                          <span className="font-medium text-secondary-foreground">
                            {item.status}
                          </span>
                        </div>
                      </div>)}
                  </CardContent>
                </Card>

                {/* Dark Expert Oversight Banner */}
                <div className="rounded-xl bg-primary text-primary-foreground p-6 shadow-lg border border-secondary/30 relative overflow-hidden">
                  <div aria-hidden="true" className="absolute -right-8 -bottom-8 size-32 rounded-full bg-secondary/20 blur-xl pointer-events-none" />
                  <div className="relative z-10 space-y-3.5">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary">
                      <UserCheck className="size-4" />
                      Dedicated Expert Oversight
                    </div>
                    <p className="text-xs sm:text-sm text-primary-foreground/90 leading-relaxed">
                      Dashboards automate data gathering, but Sunil Singh manually
                      reviews your pricing strategies weekly to maximize net RevPAR.
                    </p>
                    <Button className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-medium text-xs sm:text-sm mt-2 shadow-xs" asChild>
                      <Link to="#">
                        Request a complimentary audit
                        <ChevronRight className="size-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}